import type { VisualEffect } from '../tilemap/types';

export class ChainLightningEffect implements VisualEffect {
    private bounces: { x: number; y: number }[];
    private tileToScreenCenter: (x: number, y: number) => { x: number; y: number };

    private elapsed: number = 0;
    // Make the duration scale with the number of bounces
    private duration: number;
    private isFinished: boolean = false;

    constructor(options: {
        bounces: { x: number; y: number }[];
        tileToScreenCenter: (x: number, y: number) => { x: number; y: number };
        durationMs?: number;
    }) {
        this.bounces = options.bounces;
        this.tileToScreenCenter = options.tileToScreenCenter;
        this.duration = options.durationMs ?? Math.max(300, this.bounces.length * 150);
    }

    public update(deltaSeconds: number): void {
        if (this.isFinished) return;
        this.elapsed += deltaSeconds * 1000;
        if (this.elapsed >= this.duration) {
            this.isFinished = true;
        }
    }

    public renderOverlay(ctx: CanvasRenderingContext2D): void {
        if (this.isFinished) return;

        const progress = Math.min(this.elapsed / this.duration, 1.0);

        if (progress >= 1.0) {
            this.isFinished = true;
            return;
        }

        // Determine which segment is currently active
        const totalSegments = this.bounces.length - 1;
        if (totalSegments <= 0) {
            this.isFinished = true;
            return;
        }

        // A fast fade-out based on progress
        const alpha = 1.0 - progress;

        ctx.save();
        ctx.globalAlpha = alpha;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';

        // To make it look like lightning, we draw the lines with some random jitter
        // We only draw up to the current progress point.
        // Instead of drawing everything at once, we draw the whole path but jittered.
        // We draw multiple overlapping lines for a "glow" effect.

        const screenPoints = this.bounces.map((p) => this.tileToScreenCenter(p.x, p.y));

        // Glow layers
        const layers = [
            { width: 15, color: 'rgba(59, 130, 246, 0.3)' },
            { width: 8, color: 'rgba(96, 165, 250, 0.6)' },
            { width: 3, color: 'rgba(219, 234, 254, 1.0)' },
        ];

        for (const layer of layers) {
            ctx.beginPath();
            ctx.lineWidth = layer.width;
            ctx.strokeStyle = layer.color;

            // Draw the path with jitter
            ctx.moveTo(screenPoints[0].x, screenPoints[0].y);

            for (let i = 0; i < totalSegments; i++) {
                const start = screenPoints[i];
                const end = screenPoints[i + 1];

                // Add 2 intermediate points per segment for jitter
                const p1 = this.addJitter(start, end, 0.33);
                const p2 = this.addJitter(start, end, 0.66);

                ctx.lineTo(p1.x, p1.y);
                ctx.lineTo(p2.x, p2.y);
                ctx.lineTo(end.x, end.y);
            }

            ctx.stroke();
        }

        ctx.restore();
    }

    private addJitter(start: { x: number; y: number }, end: { x: number; y: number }, t: number): { x: number; y: number } {
        const baseX = start.x + (end.x - start.x) * t;
        const baseY = start.y + (end.y - start.y) * t;

        const maxOffset = 25;
        const offsetX = (Math.random() - 0.5) * maxOffset;
        const offsetY = (Math.random() - 0.5) * maxOffset;

        return { x: baseX + offsetX, y: baseY + offsetY };
    }

    public isComplete(): boolean {
        return this.isFinished;
    }
}
